/**
 * (S3 key, version) pairs → the key list and `versions` map that
 * `useStoredImageUrls` takes (epic 2026-10-23-media-url-versions).
 *
 * A stored logo or avatar is REWRITTEN in place when it is captured again: same
 * key, new bytes. Its version (the capture time in epoch seconds the API sends
 * next to the key, the same shape as Thrive photos' `checked_at`) becomes the
 * CloudFront URL's `?v=`, so the browser's cache of the old bytes is not what
 * renders. A key without a version keeps its bare URL.
 */
import { useMemo } from "react";

/** Epoch seconds. */
export type Version = number;

/** One stored image as a row carries it: its key and, when known, its version. */
export type VersionedKey = readonly [
  key: string | null | undefined,
  version?: Version | null | undefined,
];

export interface VersionedKeys {
  /** De-duplicated, sorted keys (null and empty keys dropped). */
  s3Keys: string[];
  /** Key → version, only for keys that have one. */
  versions: Map<string, Version>;
}

/** Pure: pairs → sorted unique keys + versions. When one key arrives with
 *  several versions (two rows read at different moments), the greatest wins. */
export function versionedKeys(pairs: Iterable<VersionedKey>): VersionedKeys {
  const keys = new Set<string>();
  const versions = new Map<string, Version>();
  for (const [key, version] of pairs) {
    if (!key) continue;
    keys.add(key);
    if (version === null || version === undefined) continue;
    const held = versions.get(key);
    if (held === undefined || version > held) versions.set(key, version);
  }
  return { s3Keys: [...keys].sort(), versions };
}

/** `versionedKeys`, memoised on CONTENT: callers build a new array every
 *  render, and a new `versions` map each time would rebuild every URL map
 *  downstream. Assumes a page of rows (about 100): it stringifies the list on
 *  every render to build the memo key. */
export function useVersionedKeys(pairs: readonly VersionedKey[]): VersionedKeys {
  const signature = JSON.stringify(versionedEntries(pairs));
  return useMemo(() => versionedKeys(JSON.parse(signature) as VersionedKey[]), [signature]);
}

function versionedEntries(pairs: readonly VersionedKey[]): [string, Version | null][] {
  const { s3Keys, versions } = versionedKeys(pairs);
  return s3Keys.map((k) => [k, versions.get(k) ?? null]);
}
